'use client'

import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import { useState, useEffect } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild, PopoverGroup } from '@headlessui/react'
import { useScroll } from 'ahooks'
import { usePathname } from 'next/navigation'

import { useGlobalStore } from '@/store/provider'
import { ThemeToggle } from '@/components/other/themeToggle'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { pages } from '@/settings'
import {
  Container, HeaderInfoMenu, HeaderMyMenu, HeaderVehicleMenu, HeaderResearchMenu,
  MenuLink, NavSide, Logo, Separator
} from '@/components'

export function Header() {
  const pathname = usePathname()
  const { user, scrollTopOver, setScrollTopOver } = useGlobalStore((state) => state)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const scroll = typeof window !== 'undefined' ? useScroll(document || window, (val) => val.top < 400) : { top: 0 }

  useEffect(() => {
    if (scroll?.top && scroll?.top >= 0) {
      if (scroll.top >= 100) {
        if (scrollTopOver) return
        setScrollTopOver(true)
      } else {
        if (!scrollTopOver) return
        setScrollTopOver(false)
      }
    } else if (scrollTopOver) {
      setScrollTopOver(false)
    }
  }, [scroll?.top])

  return (
    <div className={
      twMerge(
        'h-[80px] flex items-center justify-between top-0 right-0 left-0 z-20 lg:sticky backdrop-blur-xs dark:backdrop-blur-none',
        pathname !== '/' && 'bg-zinc-100/90 dark:bg-zinc-950',
        ((scroll?.top || 0) > 20) && 'lg:shadow-md bg-zinc-100/90 dark:bg-zinc-950'
      )
    }>
      {/* Side menu */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className='relative z-50 lg:hidden'>
        <DialogBackdrop transition className='fixed inset-0 bg-zinc-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0' />
        <div className='fixed inset-0 flex'>
          <DialogPanel transition className='relative mr-16 flex w-full max-w-[400px] flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full'>
            <TransitionChild>
              <div className='absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0'>
                <button type='button' onClick={() => setSidebarOpen(false)} className='-m-2.5 p-2.5' aria-label='Close Menu'>
                  <span className='sr-only'>Close sidebar</span>
                  <XMarkIcon aria-hidden='true' className='size-6 text-white' />
                </button>
              </div>
            </TransitionChild>
            <NavSide />
          </DialogPanel>
        </div>
      </Dialog>
      {/* main header */}
      <Container size='xl'>
        <PopoverGroup className='flex justify-between items-center gap-x-4'>
          <div className='flex items-center gap-x-6'>
            <button type='button' onClick={() => setSidebarOpen(true)} className="-m-2.5 p-2.5 lg:hidden" aria-label='Open Menu'>
              <span className="sr-only">Open Menu</span>
              <Bars3Icon aria-hidden='true' className='size-8 dark:text-zinc-100' />
            </button>
            <Logo size='w-24 md:w-32' />
            {
              process.env.NEXT_PUBLIC_ENV_NAME === 'uat' &&
              <div className='text-xs font-medium rounded-full bg-zinc-900 text-white px-3 py-2'>
                UAT
              </div>
            }
          </div>
          <div className='hidden lg:flex items-center gap-x-4 h-full'>
            <HeaderVehicleMenu />
            <HeaderResearchMenu />
            <MenuLink href={pages.spotted.href} className='font-semibold dark:font-normal'>{pages.spotted.name}</MenuLink>
            <MenuLink href={pages.forDealers.href} className='font-semibold dark:font-normal'>{pages.forDealers.name}</MenuLink>
            <HeaderInfoMenu />
          </div>
          <div className='flex flex-col gap-y-2 text-sm font-medium dark:font-normal'>
            <div className={twMerge('flex justify-end items-center gap-x-1 sm:gap-x-2', _.isEmpty(user) ? 'h-10' : 'h-14')}>
              <HeaderMyMenu />
              <Separator className='h-full' />
              <ThemeToggle />
            </div>
          </div>
        </PopoverGroup>
      </Container >
    </div>
  )
}
